import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';

import { parseCartItemId } from '@/lib/cart-utils';
import { trpc } from '@/lib/trpc';
import { useCartStore } from '@/stores/cart-store';

export function useSyncCartPrices() {
  const items = useCartStore(state => state.items);
  const updateItemPrice = useCartStore(state => state.updateItemPrice);
  const updateItemOption = useCartStore(state => state.updateItemOption);
  const updateItemSelectedOptions = useCartStore(state => state.updateItemSelectedOptions);
  const removeItem = useCartStore(state => state.removeItem);

  const syncedRef = useRef(false);
  const itemsLengthRef = useRef(items.length);

  const queryInput = useMemo(() => {
    return {
      items: items.map(item => parseCartItemId(item.id)),
    };
  }, [items]);

  const { data, isLoading } = useQuery({
    ...trpc.menu.validateCartPrices.queryOptions(queryInput),
    enabled: items.length > 0,
  });

  useEffect(() => {
    if (items.length !== itemsLengthRef.current) {
      syncedRef.current = false;
      itemsLengthRef.current = items.length;
    }
  }, [items.length]);

  useEffect(() => {
    if (isLoading || !data || items.length === 0 || syncedRef.current) {
      return;
    }

    syncedRef.current = true;

    const itemsToRemove: string[] = [];
    const priceUpdates: { id: string; name: string; oldPrice: number; newPrice: number }[] = [];
    const optionUpdates: { id: string; newOption: string }[] = [];
    const selectedOptionUpdates: {
      id: string;
      selectedOptions: NonNullable<(typeof items)[number]['selectedOptions']>;
    }[] = [];

    items.forEach(item => {
      const { menuId, optionIds } = parseCartItemId(item.id);
      const menuData = data.menus[menuId];

      if (!menuData || !menuData.isAvailable) {
        itemsToRemove.push(item.id);
        return;
      }

      const basePrice = menuData.price;
      const optionTotal = optionIds.reduce((sum, optId) => sum + (data.options[optId]?.additionalPrice ?? 0), 0);
      const newPrice = basePrice + optionTotal;

      if (newPrice !== item.price) {
        priceUpdates.push({
          id: item.id,
          name: item.name,
          oldPrice: item.price,
          newPrice,
        });
      }

      // Rebuild option display text
      if (optionIds.length > 0) {
        const newOptionTexts = optionIds
          .map(optId => {
            const opt = data.options[optId];
            if (!opt) return null;
            return opt.additionalPrice > 0
              ? `${opt.optionValue} (+${opt.additionalPrice.toLocaleString()}원)`
              : opt.optionValue;
          })
          .filter((text): text is string => text !== null);

        const newOptionText = newOptionTexts.join(', ');
        if (item.option !== newOptionText && newOptionText) {
          optionUpdates.push({ id: item.id, newOption: newOptionText });
        }

        if (item.selectedOptions) {
          const nextSelectedOptions = item.selectedOptions.map(option => {
            const currentOption = data.options[option.option_id];
            return currentOption
              ? {
                  ...option,
                  option_value: currentOption.optionValue,
                  additional_price: currentOption.additionalPrice,
                }
              : option;
          });

          if (JSON.stringify(nextSelectedOptions) !== JSON.stringify(item.selectedOptions)) {
            selectedOptionUpdates.push({ id: item.id, selectedOptions: nextSelectedOptions });
          }
        }
      }
    });

    itemsToRemove.forEach(itemId => {
      const item = items.find(i => i.id === itemId);
      if (item) {
        removeItem(itemId);
        toast.error(`'${item.name}'이(가) 판매 중단되어 장바구니에서 제거되었습니다.`);
      }
    });

    priceUpdates.forEach(update => {
      updateItemPrice(update.id, update.newPrice);
      toast(`'${update.name}'의 가격이 변경되었습니다.`);
    });

    optionUpdates.forEach(update => {
      updateItemOption(update.id, update.newOption);
    });

    selectedOptionUpdates.forEach(update => {
      updateItemSelectedOptions(update.id, update.selectedOptions);
    });
  }, [data, isLoading, items, removeItem, updateItemPrice, updateItemOption, updateItemSelectedOptions]);

  return { isLoading };
}
